/**
 * Le service de la file du soir.
 *
 * Une seule lecture, et elle ne possède rien : elle assemble quatre signaux
 * qui existent tous ailleurs. C'est le même parti que le planning et le
 * pilotage — une facture échue lue ici doit être la facture échue lue sur
 * l'écran des factures, sans qu'une seconde requête ait à en dire autre chose.
 *
 * Rien n'y écrit. La préparation d'une action passe par `preparer_action`, le
 * chemin que tout le reste du produit emprunte : la file fournit la charge,
 * elle ne tient pas la plume — ni sur le résumé montré au patron, ni sur
 * l'empreinte signée.
 */
import { z } from "zod";
import { ActionLevel } from "../../levels.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { EveningItem } from "./types.js";

export const readEveningQueueInput = z.object({
  confirmationId: z.string().optional(),
});

export type ReadEveningQueueInput = z.infer<typeof readEveningQueueInput>;

export interface EveningRepository {
  queue(ctx: ExecutionContext): Promise<EveningItem[]>;
}

export function createEveningServices(repo: EveningRepository) {
  /**
   * Ouverte à tout rôle de bureau, et fermée à l'ouvrier par la RLS des tables
   * qu'elle lit — pas par un `allowedRoles`.
   *
   * Le conducteur a un intérêt réel à voir ce qui attend : c'est souvent lui
   * qui appellera le client. Il ne pourra pas exécuter la relance pour autant —
   * `preparer_action` la soumettra au patron, qui seul confirme. Le
   * cloisonnement vit là où il est déjà écrit, pas en double ici.
   */
  const lireFileDuSoir = defineService<ReadEveningQueueInput, EveningItem[]>({
    name: "lire_file_du_soir",
    level: ActionLevel.Read,
    handler: (ctx) => repo.queue(ctx),
  });

  return { lireFileDuSoir };
}

export type EveningServices = ReturnType<typeof createEveningServices>;
